import React, { useCallback, useState } from 'react';
import { Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'enums/sizes';
import { Button } from 'components/ui/Button';
import { ReferentialKey } from 'api/referential';
import { useReferentialUtils } from 'features/referentials/hooks/useReferentialUtils';
import { PlateformesPickerForm } from './PlateformesPickerForm';

interface LinkPlateformesActionProps {
  /** Titre du dialog (ex. « Plateformes du tunnel · … »). */
  title: string;
  /**
   * Résout les codes de plateforme déjà liés, pour présélection. La commande de lien
   * REMPLACE l'ensemble : sans présélection, valider retirerait les plateformes
   * existantes. Peut être synchrone (données de la ligne) ou async (appel dédié).
   */
  resolveInitialCodes?: () => Promise<string[]> | string[];
  /** Persiste la nouvelle liste (reçoit des IDs de plateforme). */
  onLink: (plateformeIds: string[]) => Promise<void>;
  /**
   * Résout les codes de plateforme SÉLECTIONNABLES (règle métier : les
   * plateformes d'un tunnel ⊆ celles de son produit). Absent = pas de
   * restriction (cas produit).
   */
  resolveIncludeCodes?: () =>
    | Promise<string[] | undefined>
    | string[]
    | undefined;
  /** Rafraîchissement après enregistrement (ex. refetch de la liste). */
  onSuccess?: () => void;
}

/**
 * Bouton d'action (colonne Actions) qui ouvre le sélecteur de plateformes en
 * cards pour une ligne (tunnel, produit…). Mutualise la conversion code ↔ id
 * du référentiel Plateforme et la présélection de l'existant.
 */
export const LinkPlateformesAction: React.FC<LinkPlateformesActionProps> = ({
  title,
  resolveInitialCodes,
  onLink,
  resolveIncludeCodes,
  onSuccess
}) => {
  const { openDialog } = useDialog();
  const { getReferentialCodeById } = useReferentialUtils();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (selectedCodes: string[]) => {
      const plateformeIds = selectedCodes.flatMap(code => {
        const id = getReferentialCodeById(ReferentialKey.Plateforme, code);
        return id ? [id] : [];
      });
      await onLink(plateformeIds);
    },
    [getReferentialCodeById, onLink]
  );

  const open = useCallback(async () => {
    setLoading(true);
    let initialSelectedCodes: string[] = [];
    let includeCodes: string[] | undefined;
    try {
      initialSelectedCodes = (await resolveInitialCodes?.()) ?? [];
      includeCodes = await resolveIncludeCodes?.();
    } catch (error) {
      console.error('Impossible de charger les plateformes liées', error);
      toast.error('Impossible de charger les plateformes liées.');
    } finally {
      setLoading(false);
    }
    openDialog(
      { title, size: Sizes['3xl'] },
      <PlateformesPickerForm
        includeCodes={includeCodes}
        initialSelectedCodes={initialSelectedCodes}
        onSubmit={handleSubmit}
        onSuccess={onSuccess}
      />
    );
  }, [
    openDialog,
    title,
    resolveInitialCodes,
    resolveIncludeCodes,
    handleSubmit,
    onSuccess
  ]);

  return (
    <Button
      color="slate"
      round
      disabled={loading}
      onClick={open}
      tooltip="Gérer les plateformes"
      aria-label="Gérer les plateformes"
    >
      <Building2 size={16} />
    </Button>
  );
};

export default LinkPlateformesAction;
